import type { CategoryId } from '../domain/types';

// DE: Namen der Essensbilder in public/food/ (Fluent Emoji, flach, MIT; siehe LICENSE.txt dort).
// EN: Names of the food pictures in public/food/ (Fluent Emoji, flat, MIT; see LICENSE.txt there).
// prettier-ignore
export const FOOD_PICTURES = [
  'bacon', 'banana', 'basket', 'beans', 'bell-pepper', 'bento-box', 'blueberries',
  'bowl-with-spoon', 'bread', 'broccoli', 'burrito', 'butter', 'canned-food', 'carrot',
  'cheese-wedge', 'coconut', 'cooked-rice', 'cooking', 'cucumber', 'curry-rice', 'cut-of-meat',
  'ear-of-corn', 'egg', 'fish', 'flatbread', 'garlic', 'ginger-root', 'glass-of-milk',
  'green-salad', 'herb', 'honey-pot', 'hot-dog', 'ice', 'leafy-green', 'lemon', 'mushroom',
  'olive', 'onion', 'pancakes', 'pea-pod', 'pot-of-food', 'potato', 'poultry-leg', 'red-apple',
  'salt', 'sandwich', 'shallow-pan-of-food', 'sheaf-of-rice', 'shopping-cart', 'shrimp',
  'spaghetti', 'steaming-bowl', 'tomato', 'wine-glass',
] as const;

export type FoodPicture = (typeof FOOD_PICTURES)[number];

// DE: Ein Bild pro eingebautem Rezept.
// EN: One picture per built-in recipe.
// prettier-ignore
export const RECIPE_PICTURES: Readonly<Record<string, FoodPicture>> = {
  fried_rice: 'cooked-rice', mushroom_risotto: 'cooked-rice', pea_salmon_risotto: 'cooked-rice',
  chickpea_curry: 'curry-rice', tofu_curry: 'curry-rice',
  salmon_noodles: 'steaming-bowl', shrimp_noodles: 'steaming-bowl',
  tofu_broccoli: 'bento-box', teriyaki_salmon: 'bento-box',
  thai_soup: 'pot-of-food', dal: 'pot-of-food', borscht: 'pot-of-food', fish_soup: 'pot-of-food',
  minestrone: 'pot-of-food', lentil_stew: 'pot-of-food',
  buckwheat_mushrooms: 'shallow-pan-of-food', lazy_golubtsi: 'shallow-pan-of-food',
  bean_stew: 'shallow-pan-of-food', cabbage_stew: 'shallow-pan-of-food',
  veggie_bake: 'shallow-pan-of-food', shakshuka: 'shallow-pan-of-food',
  zucchini_fritters: 'pancakes', syrnyky: 'pancakes', oat_pancakes: 'pancakes',
  fish_potato_bake: 'fish', fish_spinach_mash: 'fish',
  lavash_roll: 'burrito',
  tuna_pasta: 'spaghetti', salmon_cream_pasta: 'spaghetti', pasta_ceci: 'spaghetti',
  baked_pasta_broccoli: 'spaghetti',
  frittata: 'cooking', bauernfruehstueck: 'cooking', eggs_toast: 'cooking', eggs_lavash: 'cooking',
  potatoes_quark: 'potato',
  potato_salad: 'green-salad', tuna_salad: 'green-salad',
  abendbrot: 'sandwich',
  oatmeal_berries: 'bowl-with-spoon',
};

// DE: Nur Produkte mit eigenem Bild; die übrigen zeigen das Bild ihrer Kategorie.
// EN: Only products with a picture of their own; the others show their category's picture.
// prettier-ignore
export const PRODUCT_PICTURES: Readonly<Record<string, FoodPicture>> = {
  potato: 'potato', onion: 'onion', carrot: 'carrot', garlic: 'garlic', ginger: 'ginger-root',
  tomato: 'tomato', cucumber: 'cucumber', paprika: 'bell-pepper', broccoli: 'broccoli',
  cabbage: 'leafy-green', lettuce: 'leafy-green', spinach: 'leafy-green',
  mushrooms: 'mushroom', dill: 'herb', herbs: 'herb', lemon: 'lemon', apple: 'red-apple',
  banana: 'banana',
  chicken_breast: 'poultry-leg', chicken_thighs: 'poultry-leg',
  mince_mixed: 'cut-of-meat', mince_beef: 'cut-of-meat', pork_schnitzel: 'cut-of-meat',
  sausages: 'hot-dog', bacon: 'bacon',
  smoked_salmon: 'fish', salmon: 'fish', pollock: 'fish', tuna: 'fish', shrimp: 'shrimp',
  berries: 'blueberries', peas: 'pea-pod',
  eggs: 'egg', milk: 'glass-of-milk', butter: 'butter',
  cheese: 'cheese-wedge', parmesan: 'cheese-wedge', mozzarella: 'cheese-wedge', feta: 'cheese-wedge',
  spaghetti: 'spaghetti', penne: 'spaghetti', pasta_wholegrain: 'spaghetti',
  rice_noodles: 'steaming-bowl', rice: 'cooked-rice',
  lentils: 'beans', chickpeas: 'beans', kidney_beans: 'beans',
  bread: 'bread', toast: 'bread', toast_wholegrain: 'bread', lavash: 'flatbread', tortillas: 'flatbread',
  canned_tomatoes: 'tomato', tomato_paste: 'tomato', corn: 'ear-of-corn', coconut_milk: 'coconut',
  honey: 'honey-pot', oil: 'olive', salt: 'salt', wine_red: 'wine-glass', wine_white: 'wine-glass',
};

// DE: Bild jeder Ladenkategorie.
// EN: Picture of every shop category.
export const CATEGORY_PICTURES: Readonly<Record<CategoryId, FoodPicture>> = {
  veg: 'carrot',
  meat: 'cut-of-meat',
  frozen: 'ice',
  dairy: 'glass-of-milk',
  grain: 'sheaf-of-rice',
  canned: 'canned-food',
  basics: 'salt',
  drinks: 'wine-glass',
};
